<template>
  <div class="lang-switcher" @keydown.escape="close">
    <button
      class="switcher-trigger"
      data-test="switcher-trigger"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-label="$t('languageSwitcher.switchTo')"
      @click="isOpen = !isOpen"
    >
      <img :src="activeFlagSrc" :alt="activeAltText" class="language-icon" />
      <font-awesome-icon icon="fa-solid fa-chevron-down" class="chevron" />
    </button>

    <div v-if="isOpen" class="switcher-backdrop" @click="close"></div>
    <div
      v-if="isOpen"
      ref="panelRef"
      class="switcher-panel"
      role="listbox"
      @keydown.down.prevent="moveFocus(1)"
      @keydown.up.prevent="moveFocus(-1)"
    >
      <button
        v-for="code in heldLanguages"
        :key="code"
        class="switcher-item"
        data-test="switcher-language"
        :data-code="code"
        role="option"
        :aria-current="code === displayedLanguage ? 'true' : 'false'"
        @click="onPick(code)"
      >
        <img :src="flagsStore.getFlag(code)" :alt="languageAlt(code)" class="language-icon" />
        <span class="lang-name">{{ languageName(code) }}</span>
        <font-awesome-icon
          v-if="code === displayedLanguage"
          icon="fa-solid fa-check"
          class="active-check"
        />
      </button>
      <button class="switcher-item switcher-add" data-test="switcher-add" @click="onAdd">
        <font-awesome-icon icon="fa-solid fa-plus" />
        <span class="lang-name">{{ $t('languageSwitcher.addLanguage') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { ref as vueRef } from "vue";
import { flagsStore } from "@/store/countryFlags.js";
import { useLearningLanguages } from "@/composables/useLearningLanguages.js";

// Desktop sidebar-footer switcher (dropdown). The mobile surface is the
// tab-bar flag item + BottomSheet in BottomTabBar.vue.
export default {
  name: "LearningLanguageSwitcher",
  emits: ["add-language", "upgrade-required"],
  setup(_props, { emit }) {
    const {
      heldLanguages,
      displayedLanguage,
      isMultiLingual,
      activeFlagSrc,
      activeAltText,
      languageName,
      languageAlt,
      switchTo,
    } = useLearningLanguages();
    const isOpen = vueRef(false);
    const panelRef = vueRef(null);

    const close = () => (isOpen.value = false);

    const moveFocus = (direction) => {
      if (!panelRef.value) return;
      const items = Array.from(
        panelRef.value.querySelectorAll(".switcher-item")
      );
      if (items.length === 0) return;
      const currentIndex = items.indexOf(document.activeElement);
      // Focus may still sit on the trigger when the panel opens: ArrowDown
      // enters at the top, ArrowUp at the bottom.
      const nextIndex =
        currentIndex === -1
          ? direction === 1
            ? 0
            : items.length - 1
          : (currentIndex + direction + items.length) % items.length;
      items[nextIndex].focus();
    };

    const onPick = (code) => {
      close();
      switchTo(code);
    };

    const onAdd = () => {
      close();
      emit(isMultiLingual.value ? "add-language" : "upgrade-required");
    };

    return {
      isOpen,
      panelRef,
      close,
      moveFocus,
      onPick,
      onAdd,
      heldLanguages,
      displayedLanguage,
      activeFlagSrc,
      activeAltText,
      languageName,
      languageAlt,
      flagsStore,
    };
  },
};
</script>

<style scoped>
.lang-switcher {
  position: relative;
}
.switcher-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  min-width: 44px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--al-text-muted);
}
.language-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
}
.switcher-panel {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  min-width: 220px;
  background: var(--al-surface);
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius);
  box-shadow: var(--al-shadow-md, 0 8px 24px rgba(0, 0, 0, 0.16));
  padding: 4px;
  z-index: 1050;
}
.switcher-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 44px;
  padding: 8px 12px;
  background: transparent;
  border: none;
  border-radius: var(--al-radius);
  cursor: pointer;
  color: var(--al-text);
  text-align: left;
}
.switcher-item:hover,
.switcher-item:focus-visible {
  background: var(--al-surface-sunken);
}
.active-check {
  margin-left: auto;
  color: var(--al-primary);
}
.switcher-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1040;
}
</style>
